'use client';

import { CSSProperties, FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { Building2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { useWorkspaceTypeFilter } from '@/hotdesk-ui/hooks/useWorkspaceTypeFilter';
import { animateScrollLeft } from '@/hotdesk-ui/helpers/scroll-animation';
import { onPageVisibleToUser } from '@/hotdesk-ui/helpers/page-visibility';
import type { ListingTypeItem } from '@/hotdesk-ui/store/slices/listing-types.slice';
import styles from './Navbar.module.scss';

// The three the design leads with, in this order: Desk, Meeting Room, Office
// Room. Everything else follows them and is what hovering reveals — which is
// whichever ids the `HD_LISTING_TYPES_KEY` remote config lets through, since
// that is what ListingTypeService asks the API for. Matching on id rather than
// `type` keeps the order stable in every locale, as the API translates the
// labels.
const LEAD_TYPE_IDS = [1, 2, 3];

// "Workspaces" + the lead types. The collapsed track is exactly this many
// pills wide, so the bar looks the same at rest as it always has.
const COLLAPSED_BUTTONS = 1 + LEAD_TYPE_IDS.length;

// Mount hint timings. The bar shows four pills but holds more, so on load it
// scrolls itself through the hidden ones and slides straight back to the first
// choices — a quick "there is more in here" before the user has to guess.
const HINT_DELAY_MS = 400; // let the entrance animation land first
const HINT_TRAVEL_MS = 420; // each leg, out and back
const HINT_HOLD_MS = 200; // beat at the far end so the reveal registers
const HINT_PEEK_PX = 220; // roughly a pill and a half of the hidden tail

// The hint belongs to the page load, not to the element: the switcher
// re-mounts every time the header search hands the centre slot back on scroll
// up, and replaying it there would be noise.
let hintPlayed = false;
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
interface TrackWidths {
  /** Width of the first `COLLAPSED_BUTTONS` pills, gaps included. */
  collapsed: number;
  /** Width of every pill — what hover (and the mount hint) opens to. */
  expanded: number;
}
export interface WorkspaceTypeSwitcherProps {
  /**
   * Fires when the bar opens to the full set and when it closes again. The
   * header uses it to get "+ List your workspace" out of the way, which is
   * what lets the widened bar sit centred instead of being shouldered left
   * by the action cluster.
   */
  onExpandedChange?: (expanded: boolean) => void;
}
const WorkspaceTypeSwitcher: FC<WorkspaceTypeSwitcherProps> = ({
  onExpandedChange
}) => {
  const t = useTranslations();
  const listingTypes = useAppSelector((state: RootState) => state.listingTypes.items);
  const {
    selectedTypes,
    selectAll,
    toggleType
  } = useWorkspaceTypeFilter();
  const barRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [widths, setWidths] = useState<TrackWidths | null>(null);
  const [hovered, setHovered] = useState(false);
  // Latches true the first time there is something to reveal, and never goes
  // back. The hint effect hangs off this rather than off `widths` so that a
  // re-measure part-way through (the web font swapping in) can't tear down a
  // running hint and leave the bar stranded mid-scroll.
  const [hintReady, setHintReady] = useState(false);

  // Cancels the in-flight leg of the mount hint. The moment the user touches
  // the bar the hint has done its job, and letting it keep writing
  // `scrollLeft` would fight whatever they are doing.
  const hintRef = useRef<(() => void) | null>(null);
  const stopHint = useCallback(() => {
    hintRef.current?.();
    hintRef.current = null;
  }, []);
  const orderedTypes = useMemo(() => {
    const lead = LEAD_TYPE_IDS.map(id => listingTypes.find(lt => lt.id === id)).filter((lt): lt is ListingTypeItem => Boolean(lt));
    const rest = listingTypes.filter(lt => !LEAD_TYPE_IDS.includes(lt.id));
    return [...lead, ...rest];
  }, [listingTypes]);
  const buttonCount = 1 + (orderedTypes.length || LEAD_TYPE_IDS.length);

  // Both widths come off the laid-out track rather than a formula, so they
  // survive translated labels and the extra types. `offsetLeft`/`offsetWidth`
  // (not rects) because the bar animates in under a `scale()` transform,
  // which would skew a `getBoundingClientRect()` reading mid-entrance.
  const measure = useCallback(() => {
    const bar = barRef.current;
    const track = trackRef.current;
    if (!bar || !track) return;
    const pills = Array.from(track.children) as HTMLElement[];
    if (pills.length === 0) return;
    const lastVisible = pills[Math.min(COLLAPSED_BUTTONS, pills.length) - 1];
    const style = getComputedStyle(bar);
    // The bar is `border-box`, so its width has to carry its own padding
    // and border on top of the track it wraps.
    const chrome = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
    const next: TrackWidths = {
      collapsed: lastVisible.offsetLeft + lastVisible.offsetWidth + chrome,
      expanded: track.offsetWidth + chrome
    };
    setWidths(prev => prev && prev.collapsed === next.collapsed && prev.expanded === next.expanded ? prev : next);
    if (next.expanded > next.collapsed + 1) setHintReady(true);
  }, []);

  // Layout effect so the first paint is already collapsed — measuring after
  // paint would flash the full-width track.
  useIsomorphicLayoutEffect(() => {
    measure();
  }, [measure, buttonCount]);

  // Labels change width when the web font swaps in, which moves both
  // numbers.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(track);
    return () => observer.disconnect();
  }, [measure]);

  // Mount hint: scroll the collapsed bar into the hidden types and back to
  // the first choices. Waits on `hintReady`, which the measurement latches
  // once the bar has been clipped and the types have arrived — before that
  // there is no overflow to travel through.
  useEffect(() => {
    if (!hintReady || hintPlayed) return;
    const bar = barRef.current;
    if (!bar) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let outTimer = 0;
    let holdTimer = 0;
    const play = () => {
      // Geometry is read here, not when the effect ran: the splash can
      // sit over the page for seconds, and the bar may have been
      // re-measured in the meantime.
      const home = bar.scrollLeft;
      const runway = bar.scrollWidth - bar.clientWidth - home;
      if (runway <= 1) return;

      // Spend the latch here rather than in the effect body. React
      // StrictMode runs effects mount → cleanup → mount in dev, so a
      // flag set up front is burnt by the pass that then gets torn
      // down, and the hint never plays at all.
      hintPlayed = true;
      const peek = home + Math.min(runway, HINT_PEEK_PX);
      stopHint();
      // Each leg is chained off the previous one finishing. Scheduling
      // both up front looks equivalent and is not: hydration blocks the
      // main thread for over a second here, both timers fall due while
      // it is blocked, and the return leg then lands ~17ms after the
      // outbound one — cancelling it before a single frame is drawn.
      hintRef.current = animateScrollLeft(bar, peek, HINT_TRAVEL_MS, () => {
        holdTimer = window.setTimeout(() => {
          hintRef.current = animateScrollLeft(bar, home, HINT_TRAVEL_MS);
        }, HINT_HOLD_MS);
      });
    };

    // Nothing to show while the splash is over the page — on a cold load
    // it is still up well past the point the switcher has measured itself.
    const stopWaiting = onPageVisibleToUser(() => {
      outTimer = window.setTimeout(play, HINT_DELAY_MS);
    });
    return () => {
      stopWaiting();
      window.clearTimeout(outTimer);
      window.clearTimeout(holdTimer);
      stopHint();
    };
  }, [hintReady, stopHint]);

  // A type past the fourth pill is hidden once the bar settles back, so keep
  // the selected one in the scrolled-to window. Done by hand rather than
  // with `scrollIntoView`, which would also scroll the page behind the
  // fixed header. Scrolls the least it can, so a pill that is already fully
  // in view is left where it is.
  const keepActiveInView = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;
    const active = bar.querySelector<HTMLElement>(`.${styles.typeBtnActive}`);
    if (!active) return;
    // Pill offsets are read against the track, which starts at the bar's
    // *content* box; `scrollLeft` is measured from its padding box. So the
    // bar's own padding has to be added back on the right, or the resting
    // position lands the pill's edge level with the padding box and the
    // rounded black edge crops the white active chip. The left branch
    // needs no such term: the two paddings cancel on that side.
    const barStyle = getComputedStyle(bar);
    const padLeft = parseFloat(barStyle.paddingLeft) || 0;
    const padRight = parseFloat(barStyle.paddingRight) || 0;
    const left = active.offsetLeft;
    const right = left + active.offsetWidth + padLeft + padRight;
    if (left < bar.scrollLeft) {
      bar.scrollLeft = left;
    } else if (right > bar.scrollLeft + bar.clientWidth) {
      bar.scrollLeft = right - bar.clientWidth;
    }
  }, []);
  useEffect(() => {
    keepActiveInView();
  }, [keepActiveInView, selectedTypes, widths]);

  // Hovering a bar that is already showing everything shouldn't rearrange
  // the header, so the open state is gated on there being a tail to reveal.
  const canExpand = Boolean(widths && widths.expanded > widths.collapsed + 1);
  const expanded = hovered && canExpand;
  const width = widths ? expanded ? widths.expanded : widths.collapsed : null;

  // The hover reveal is a `width` animation, and a bar wide enough for every
  // pill has no overflow left to scroll: the browser clamps `scrollLeft` to
  // 0 on the way open and leaves it there on the way back, so the collapsed
  // bar would return to "Workspaces, Desk, …" with the selected type out of
  // sight again. Re-seating it once at the end would snap, so instead the
  // pill is held in view on every frame of the close — as the bar narrows,
  // the scroll range grows under it and the selection slides back into the
  // window with the same motion.
  const wasExpanded = useRef(false);
  useEffect(() => {
    const closing = wasExpanded.current && !expanded;
    wasExpanded.current = expanded;
    // Only the open → closed edge. Running on mount instead would fight
    // the hint above, which is off scrolling through the hidden types.
    if (!closing) return;
    let raf = 0;
    const start = performance.now();
    const step = (now: number) => {
      keepActiveInView();
      // A shade past the 0.35s width transition, so the last frame is
      // measured against the bar at its collapsed size.
      if (now - start > 420) return;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [expanded, keepActiveInView]);

  // Through a ref so an inline arrow from the parent can't turn this into a
  // render loop.
  const notifyRef = useRef(onExpandedChange);
  notifyRef.current = onExpandedChange;
  useEffect(() => {
    notifyRef.current?.(expanded);
  }, [expanded]);
  return <div ref={barRef} className={styles.typeSwitcher} style={width === null ? undefined : {
    '--type-switcher-width': `${width}px`
  } as CSSProperties} onMouseEnter={() => {
    stopHint();
    setHovered(true);
  }} onMouseLeave={() => setHovered(false)}
  // Any hands-on scrolling — a trackpad swipe, a drag — outranks the
  // hint, which is only there for people who haven't touched it yet.
  onPointerDown={stopHint} onWheel={stopHint}
  // Keyboard equivalent of the hover reveal — tabbing into a pill
  // that is currently past the fold opens the bar around it.
  onFocus={() => {
    stopHint();
    setHovered(true);
  }} onBlur={() => setHovered(false)} data-sentry-component="WorkspaceTypeSwitcher" data-sentry-source-file="WorkspaceTypeSwitcher.tsx">
            <div ref={trackRef} className={styles.typeTrack} style={{
      gridTemplateColumns: `repeat(${buttonCount}, 1fr)`
    }}>
                <button type="button" className={`${styles.typeBtn} ${selectedTypes.length === 0 ? styles.typeBtnActive : ''}`} onClick={selectAll}>
                    <span className={styles.typeIcon}>
                        <Building2 size={18} data-sentry-element="Building2" data-sentry-source-file="WorkspaceTypeSwitcher.tsx" />
                    </span>
                    <span className={styles.typeLabel}>
                        {t('landing-v4.categories.workspaces')}
                    </span>
                </button>

                {orderedTypes.length === 0 && LEAD_TYPE_IDS.map(i => <span key={i} className={styles.typeSkeleton} />)}

                {orderedTypes.map(lt => <button key={lt.id} type="button" className={`${styles.typeBtn} ${selectedTypes.includes(lt.id) ? styles.typeBtnActive : ''}`} onClick={() => toggleType(lt.id)}>
                        {lt.icon && <span className={styles.typeIcon}>
                                <Image src={lt.icon} alt="" width={18} height={18} unoptimized />
                            </span>}
                        <span className={styles.typeLabel}>{lt.type}</span>
                    </button>)}
            </div>
        </div>;
};
export default WorkspaceTypeSwitcher;